// HTML rendering and positioning for preview popup

import { escapeHtml } from '../../../../../modules/files/file-list.utils.js';
import {
	loadPreviewPosition,
	calculatePopupPosition,
	loadPreviewSize,
	clampPreviewSize,
	PREVIEW_DEFAULT_W,
	PREVIEW_DEFAULT_H,
	PREVIEW_VIEWPORT_MARGIN
} from '../position.js';

export function renderPreviewPopupHtml(fileName, previewContent) {
	const popupHtml = `
		<div class="preview-popup-overlay" id="preview-popup-overlay" tabindex="-1">
			<div class="preview-popup">
				<div class="preview-popup__header">
					<div class="preview-popup__nav">
						<button class="preview-popup__nav-btn preview-popup__nav-prev" aria-label="Previous document" title="Previous document (Left arrow)"><i class="bi bi-chevron-left"></i></button>
						<span class="preview-popup__nav-counter" aria-live="polite"></span>
						<button class="preview-popup__nav-btn preview-popup__nav-next" aria-label="Next document" title="Next document (Right arrow)"><i class="bi bi-chevron-right"></i></button>
					</div>
					<div class="preview-popup__title">${escapeHtml(fileName)}</div>
					<div class="preview-popup__header-right">
						<button class="preview-popup__tg" aria-label="Send to Telegram" title="Send to Telegram">
							<i class="bi bi-telegram"></i>
						</button>
						<button class="preview-popup__fav" aria-label="Add to favorites" title="Add to favorites">
							<i class="bi bi-star"></i>
						</button>
						<button class="preview-popup__open" aria-label="Open in new window" title="Open in new window">
							<i class="bi bi-box-arrow-up-right"></i>
							<span class="preview-popup__open-text">Open</span>
						</button>
						<button class="preview-popup__download" aria-label="Download" title="Download this file">
							<i class="bi bi-download"></i>
						</button>
						<button class="preview-popup__copy-link" aria-label="Copy link" title="Copy link to clipboard">
							<i class="bi bi-link-45deg"></i>
						</button>
						<button class="preview-popup__pin" aria-label="Pin preview" title="Pin preview">
							<i class="bi bi-pin"></i>
						</button>
						<button class="preview-popup__close" aria-label="Close">
							<i class="bi bi-x-lg"></i>
						</button>
					</div>
				</div>
				<div class="preview-popup__content">
					${previewContent}
				</div>
				<div class="preview-popup__resize-handle" aria-hidden="true" title="Resize"></div>
			</div>
		</div>
	`;

	return popupHtml;
}

export function generatePreviewContent(file, isPDF) {
	if (isPDF) {
		// For PDF, start with loading spinner - PDF will be loaded via fetch
		return `
			<div class="preview-popup__loading">
				<div class="spinner-ultra" aria-label="Loading" title="Loading"></div>
			</div>
		`;
	} else {
		// For other files, use regular iframe
		// Add tabindex="-1" so iframe can receive focus programmatically
		return `
			<iframe src="" frameborder="0" class="preview-popup__iframe" loading="lazy" tabindex="-1"></iframe>
			<div class="preview-popup__loading">
				<div class="spinner-ultra" aria-label="Loading" title="Loading"></div>
			</div>
		`;
	}
}

export function positionPreviewPopup(popupElement, icon, wasPinned) {
	// Start from the user's saved size (or default), then fail-safe clamp to the
	// current viewport so a size saved on a big screen never overflows a small one.
	const savedSize = loadPreviewSize();
	const desiredW = savedSize && savedSize.width ? savedSize.width : PREVIEW_DEFAULT_W;
	const desiredH = savedSize && savedSize.height ? savedSize.height : PREVIEW_DEFAULT_H;
	const clamped = clampPreviewSize(desiredW, desiredH, PREVIEW_VIEWPORT_MARGIN, PREVIEW_VIEWPORT_MARGIN);
	const popupWidth = clamped.width;
	const popupHeight = clamped.height;

	let cursorX = null;
	let cursorY = null;

	if (icon && icon.length) {
		const iconRect = icon[0].getBoundingClientRect();
		cursorX = iconRect.left + iconRect.width / 2;
		cursorY = iconRect.top + iconRect.height / 2;
	}

	const savedPosition = loadPreviewPosition();
	const { left, top } = calculatePopupPosition(savedPosition, cursorX, cursorY, popupWidth, popupHeight, icon);

	popupElement.css({
		position: 'fixed',
		left: left + 'px',
		top: top + 'px',
		width: popupWidth + 'px',
		height: popupHeight + 'px',
		background: 'transparent',
		backdropFilter: 'none'
	});
	popupElement.find('.preview-popup').css({
		width: '100%',
		height: '100%',
		margin: 0,
		maxWidth: 'none',
		maxHeight: 'none'
	});
}

export function setupOverscrollBehavior() {
	// Use CSS overscroll-behavior on html/body to prevent scroll chaining
	const originalHtmlOverscroll = document.documentElement.style.overscrollBehavior;
	const originalBodyOverscroll = document.body.style.overscrollBehavior;
	document.documentElement.style.overscrollBehavior = 'none';
	document.body.style.overscrollBehavior = 'none';

	return {
		originalHtmlOverscroll,
		originalBodyOverscroll
	};
}

export function restoreOverscrollBehavior(originalValues) {
	if (originalValues.originalHtmlOverscroll !== undefined) {
		document.documentElement.style.overscrollBehavior = originalValues.originalHtmlOverscroll || '';
	}
	if (originalValues.originalBodyOverflow !== undefined) {
		document.body.style.overscrollBehavior = originalValues.originalBodyOverflow || '';
	}
}
